import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  sendPasswordResetEmail,
  onAuthStateChanged,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import API_CONFIG, { getApiUrl } from '../config/api';
const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isNewUser, setIsNewUser] = useState(false);
  const [adminImpersonation, setAdminImpersonation] = useState(null);

  // Add a new state variable for onboarding status
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);

  const signup = async (email, password) => {
    try {
      const result = await createUserWithEmailAndPassword(auth, email, password);
      setIsNewUser(true);

      return { user: result.user, error: null };
    } catch (error) {
      console.error('Signup error:', error);
      return { user: null, error: error };
    }
  };

  const login = async (email, password) => {
    try {
      const result = await signInWithEmailAndPassword(auth, email, password);
      return { user: result.user, error: null };
    } catch (error) {
      console.error('Login error:', error);
      return { user: null, error: error };
    }
  };

  const loginWithGoogle = async () => {
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const isNewGoogleUser = result._tokenResponse?.isNewUser;
      setIsNewUser(isNewGoogleUser);

      // Always save/update user data regardless of whether they're new or existing
      // This ensures user data is always in sync between Firebase and our database
      const userData = {
        id: result.user.uid,
        email: result.user.email,
        first_name: result.user.displayName?.split(' ')[0] || '',
        last_name: result.user.displayName?.split(' ').slice(1).join(' ') || '',
        display_name: result.user.displayName || '',
        // Add referrer URL if available
        referrer_url: document.referrer || window.location.href
      };

      console.log('Saving Google user data:', userData); // Debug log

      const response = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.AUTH.SAVE_USER), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(userData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Save user API error:', response.status, errorText);
        throw new Error(`Failed to save user data: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      console.log('Save user response:', data); // Debug log

      if (!data.success) {
        console.error('Save user failed:', data.error);
        throw new Error(data.error || 'Failed to save user data');
      }

      return {
        user: result.user,
        error: null,
        isNewUser: isNewGoogleUser
      };
    } catch (error) {
      console.error('Google login error:', error); // Enhanced debug log

      // Don't fail the entire login process if user saving fails
      // The user is still authenticated with Firebase
      if (error.message.includes('Failed to save user data')) {
        console.warn('User authenticated but not saved to database:', error.message);
        // You might want to show a warning to the user or retry later
      }

      return {
        user: null,
        error: error.message
      };
    }
  };
  const logout = () => {
    // Clear admin impersonation if active
    if (adminImpersonation) {
      localStorage.removeItem('adminImpersonation');
      setAdminImpersonation(null);
      window.location.href = '/app/login';
      return Promise.resolve();
    }
    return signOut(auth);
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
      return { error: null };
    } catch (error) {
      console.error('Reset password error:', error);
      return { error: error };
    }
  };

  useEffect(() => {
    const setAuthPersistence = async () => {
      await setPersistence(auth, browserLocalPersistence);
    };
    setAuthPersistence();

    // Check for admin impersonation
    const checkAdminImpersonation = () => {
      const impersonationData = localStorage.getItem('adminImpersonation');
      if (impersonationData) {
        try {
          const data = JSON.parse(impersonationData);
          // Check if impersonation is still valid (within 24 hours)
          const isValid = Date.now() - data.timestamp < 24 * 60 * 60 * 1000;
          if (isValid && data.isAdminImpersonation) {
            setAdminImpersonation(data);
            setHasCompletedOnboarding(true); // Assume admin impersonated users have completed onboarding
            setLoading(false);
            return true;
          } else {
            localStorage.removeItem('adminImpersonation');
          }
        } catch (error) {
          console.error('Error parsing admin impersonation data:', error);
          localStorage.removeItem('adminImpersonation');
        }
      }
      return false;
    };

    // If admin impersonation is active, don't proceed with Firebase auth
    if (checkAdminImpersonation()) {
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      setAdminImpersonation(null); // Clear any admin impersonation when real user logs in

      // Check onboarding status whenever a user logs in
      if (currentUser) {
        try {
          const checkResponse = await fetch(getApiUrl(API_CONFIG.ENDPOINTS.AUTH.CHECK_USER_ONBOARDING), {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              user_id: currentUser.uid
            })
          });

          const checkData = await checkResponse.json();
          setHasCompletedOnboarding(checkData.success && checkData.hasCompletedOnboarding);
        } catch (error) {
          console.error('Error checking onboarding status:', error);
          setHasCompletedOnboarding(false);
        }
      } else {
        setHasCompletedOnboarding(false);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Helper function to get current user (either real user or admin impersonation)
  const getCurrentUser = () => {
    if (adminImpersonation) {
      return {
        uid: adminImpersonation.user_id,
        email: adminImpersonation.email,
        displayName: adminImpersonation.display_name,
        isAdminImpersonation: true
      };
    }
    return user;
  };

  // Add hasCompletedOnboarding to the context value
  const value = {
    user,
    loading,
    isNewUser,
    hasCompletedOnboarding,
    adminImpersonation,
    getCurrentUser,
    signup,
    login,
    loginWithGoogle,
    logout,
    resetPassword
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
